import Phaser from 'phaser';
import { SCENE_KEYS, GAME_CONFIG, screenShakeState } from '@/config/constants';
import { AudioManager } from '@/utils/AudioManager';
import { transitionTo, fadeIn } from '@/utils/UIAnimations';
import { COLORS, COLORS_HEX, FONTS, RADIUS, TEXT_PRESET } from '@/ui/theme';
import { GamepadNav } from '@/utils/GamepadNav';
import { createPillButton } from '@/ui/PillButton';
import { drawSelectionHalo } from '@/ui/SelectionHalo';

interface Focusable {
  x: number; y: number; w: number; h: number;
  focus: (on: boolean) => void;
  confirm?: () => void;
  left?: () => void;
  right?: () => void;
  // Set only for the pill CTA button: its real (elliptical) cap radii.
  capRx?: number;
  capRy?: number;
}

/**
 * Settings Scene — Crystal-energy run calibration shell
 */
export class SettingsScene extends Phaser.Scene {
  private nav?: GamepadNav;
  private focusables: Focusable[] = [];
  private focusIdx = -1;
  private focusHalo?: Phaser.GameObjects.Graphics;

  constructor() {
    super({ key: SCENE_KEYS.SETTINGS });
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;
    const cy = HEIGHT / 2;
    const audio = AudioManager.getInstance();
    const settings = audio.getMixerSettings();
    fadeIn(this, 400);

    this.focusables = [];

    const videoKey = 'bg_video_world1_video';
    if (this.cache.video.has(videoKey)) {
      const videoBg = this.add.video(cx, cy, videoKey);
      videoBg.setDepth(0).setScale(1).setVolume(0).play(true).setAlpha(0.64);
    }

    const bg = this.add.graphics().setDepth(1);
    bg.fillGradientStyle(COLORS.midnightDepth, COLORS.midnightDepth, COLORS.deepOcean, COLORS.deepOcean, 0.9, 0.9, 0.98, 0.98);
    bg.fillRect(0, 0, WIDTH, HEIGHT);

    const glows = this.add.graphics().setDepth(2);
    glows.fillStyle(COLORS.aqua, 0.1);
    glows.fillCircle(cx, cy - 160, 260);
    glows.fillStyle(COLORS.sapphire, 0.14);
    glows.fillCircle(cx - 380, cy + 60, 220);
    glows.fillCircle(cx + 380, cy + 80, 220);

    const vignette = this.add.graphics().setDepth(3);
    vignette.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.78, 0.78, 0, 0).fillRect(0, 0, WIDTH, 150);
    vignette.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0, 0.82, 0.82).fillRect(0, HEIGHT - 150, WIDTH, 150);

    const pw = 700;
    const ph = 580;
    const pRadius = RADIUS.xl;
    const plx = cx - pw / 2;
    const pty = (HEIGHT - ph) / 2 - 20;

    const panel = this.add.graphics().setDepth(4);
    panel.fillStyle(COLORS.aqua, 0.08);
    panel.fillRoundedRect(plx - 12, pty - 10, pw + 24, ph + 24, pRadius + 10);
    panel.fillStyle(COLORS.midnightDepth, 0.5);
    panel.fillRoundedRect(plx, pty + 8, pw, ph, pRadius);
    panel.fillGradientStyle(COLORS.tidewater, COLORS.tidewater, COLORS.abyssBlue, COLORS.abyssBlue, 0.94, 0.94, 0.92, 0.92);
    panel.fillRoundedRect(plx, pty, pw, ph, pRadius);
    panel.fillStyle(COLORS.pearlWhite, 0.06);
    panel.fillRoundedRect(plx + 10, pty + 10, pw - 20, 112, pRadius - 10);
    panel.lineStyle(2, COLORS.aqua, 0.62);
    panel.strokeRoundedRect(plx, pty, pw, ph, pRadius);
    panel.lineStyle(1, COLORS.pearlWhite, 0.2);
    panel.strokeRoundedRect(plx + 4, pty + 4, pw - 8, ph - 8, pRadius - 4);
    panel.fillStyle(COLORS.aquaLight, 0.34);
    panel.fillRoundedRect(plx + 56, pty + 4, pw - 112, 3, 2);

    // Single SETTINGS title, dropped lower in the header band so it has clear
    // breathing room above it (no more cramped eyebrow + two-line stack).
    this.add.text(cx, pty + 64, 'SETTINGS', {
      ...TEXT_PRESET.section,
      fontSize: '46px',
      color: COLORS_HEX.goldLight,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(5);

    const divY = pty + 116;
    const divider = this.add.graphics().setDepth(5);
    divider.lineStyle(1, COLORS.aqua, 0.35);
    divider.lineBetween(cx - 200, divY, cx + 200, divY);
    divider.fillStyle(COLORS.aqua, 0.5);
    divider.fillTriangle(cx - 5, divY, cx, divY - 4, cx + 5, divY);
    divider.fillTriangle(cx - 5, divY, cx, divY + 4, cx + 5, divY);

    // Subtitle uses the display sans (was the serif body font, which read as
    // out of place against the rest of the panel).
    this.add.text(cx, pty + 144, 'Tune audio and feedback before the next fresh run.', {
      ...TEXT_PRESET.body,
      fontFamily: FONTS.display,
      fontSize: '22px',
      color: COLORS_HEX.textSecondary,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(5);

    const sliderStartY = pty + 250;
    this.focusables.push(this.createSlider(cx, sliderStartY, 'MUSIC VOLUME', settings.music.volume, (val) => {
      audio.setMusicVolume(val);
    }));

    this.focusables.push(this.createSlider(cx, sliderStartY + 130, 'SFX VOLUME', settings.sfx.volume, (val) => {
      audio.setSFXVolume(val);
    }));

    screenShakeState.loadFromStorage();
    const toggleY = sliderStartY + 260;
    this.focusables.push(this.createToggle(cx, toggleY, 'SCREEN SHAKE', screenShakeState.isEnabled(), (val) => {
      screenShakeState.setEnabled(val);
      audio.playSFX(this, 'sfx_btn_click', { volume: 0.5 });
    }));

    const backBtnContainer = createPillButton(this, {
      x: cx, y: pty + ph + 60,
      label: 'BACK TO MENU',
      variant: 'secondary',
      size: 'medium',
      width: 440,
      onClick: () => transitionTo(this, SCENE_KEYS.MENU),
    });
    backBtnContainer.setDepth(5);
    this.focusables.push({
      x: cx, y: pty + ph + 60, w: 440, h: 72,
      capRx: (backBtnContainer as any).pillCapRx,
      capRy: (backBtnContainer as any).pillCapRy,
      focus: () => {},
      confirm: () => (backBtnContainer as any).press?.(),
    });

    // Focus halo — drawn as a glowing rect around the currently-focused
    // control. Nothing is pre-selected (focusIdx = -1): the player must
    // deliberately move onto a focusable with the joystick / arrows / mouse
    // before any frame appears.
    this.focusHalo = this.add.graphics().setDepth(20);
    backBtnContainer.on('pointerover', () => this.setFocus(this.focusables.length - 1));

    const step = (dir: number) => {
      const n = this.focusables.length;
      if (this.focusIdx < 0) this.setFocus(dir >= 0 ? 0 : n - 1);
      else this.setFocus((this.focusIdx + dir + n) % n);
    };

    // Gamepad: Up/Down cycle focus; Left/Right adjust sliders. A and B both
    // press the focused control (so either face button acts as "click" on
    // whatever the halo is on — BACK gets pressed when focus is on BACK).
    this.nav = new GamepadNav(this, {
      onUp:    () => step(-1),
      onDown:  () => step(1),
      onLeft:  () => this.focusables[this.focusIdx]?.left?.(),
      onRight: () => this.focusables[this.focusIdx]?.right?.(),
      onConfirm: () => this.focusables[this.focusIdx]?.confirm?.(),
    });

    // Keyboard parity: arrows + Enter/Space + Escape
    const kb = this.input.keyboard;
    if (kb) {
      kb.on('keydown-UP',    () => step(-1));
      kb.on('keydown-DOWN',  () => step(1));
      kb.on('keydown-LEFT',  () => this.focusables[this.focusIdx]?.left?.());
      kb.on('keydown-RIGHT', () => this.focusables[this.focusIdx]?.right?.());
      const fire = () => this.focusables[this.focusIdx]?.confirm?.();
      kb.on('keydown-ENTER', fire);
      kb.on('keydown-SPACE', fire);
      kb.on('keydown-ESC', () => (backBtnContainer as any).press?.());
    }
  }

  update(): void {
    this.nav?.update();
  }

  private setFocus(i: number): void {
    if (i === this.focusIdx) {
      this.drawFocusHalo();
      return;
    }
    if (this.focusIdx >= 0) this.focusables[this.focusIdx]?.focus(false);
    this.focusIdx = i;
    if (this.focusIdx >= 0) this.focusables[this.focusIdx]?.focus(true);
    this.drawFocusHalo();
  }

  private drawFocusHalo(): void {
    if (!this.focusHalo) return;
    if (this.focusIdx < 0) { this.focusHalo.clear(); return; }
    const f = this.focusables[this.focusIdx];
    if (!f) return;

    // The pill CTA button offsets off its real elliptical caps so the ring
    // stays parallel to the stretched-SVG edge.
    if (f.capRx != null && f.capRy != null) {
      drawSelectionHalo(this.focusHalo, {
        cx: f.x, cy: f.y, w: f.w, h: f.h, rx: f.capRx, ry: f.capRy,
      }, { gap: 14 });
      return;
    }

    // Sliders / toggles keep a rounded-rect frame.
    this.focusHalo.clear();
    const padX = 18, padY = 14;
    const w = f.w + padX * 2, h = f.h + padY * 2;
    const x = f.x - w / 2, y = f.y - h / 2;
    const r = h / 2;
    this.focusHalo.lineStyle(3, 0x88EEFF, 0.95);
    this.focusHalo.strokeRoundedRect(x, y, w, h, r);
    this.focusHalo.lineStyle(1, 0xFFFFFF, 0.6);
    this.focusHalo.strokeRoundedRect(x + 4, y + 4, w - 8, h - 8, Math.max(r - 4, 2));
  }

  private createSlider(
    cx: number,
    y: number,
    label: string,
    initialValue: number,
    onChange: (value: number) => void,
  ): Focusable {
    const trackW = 420;
    const trackH = 14;
    const trackLeft = cx - trackW / 2;

    this.add.text(cx, y - 40, label, {
      ...TEXT_PRESET.label,
      fontSize: '24px',
      color: COLORS_HEX.aquaLight,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(5);

    const pctCenterX = cx + trackW / 2 + 40;
    const pctText = this.add.text(pctCenterX, y, `${Math.round(initialValue * 100)}%`, {
      ...TEXT_PRESET.label,
      fontSize: '26px',
      color: COLORS_HEX.goldLight,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(5);

    const trackBg = this.add.graphics().setDepth(4);
    trackBg.fillStyle(COLORS.midnightDepth, 0.85);
    trackBg.fillRoundedRect(trackLeft, y - trackH / 2, trackW, trackH, 6);
    trackBg.fillStyle(COLORS.pearlWhite, 0.06);
    trackBg.fillRoundedRect(trackLeft + 4, y - trackH / 2 + 2, trackW - 8, trackH * 0.38, 4);
    trackBg.lineStyle(1, COLORS.aqua, 0.28);
    trackBg.strokeRoundedRect(trackLeft, y - trackH / 2, trackW, trackH, 6);

    const fill = this.add.graphics().setDepth(4);
    const drawFill = (val: number) => {
      fill.clear();
      if (val > 0.01) {
        fill.fillGradientStyle(COLORS.aquaLight, COLORS.aquaLight, COLORS.sapphire, COLORS.sapphire, 0.92, 0.92, 0.92, 0.92);
        fill.fillRoundedRect(trackLeft, y - trackH / 2, trackW * val, trackH, 6);
      }
    };
    drawFill(initialValue);

    const handleX = trackLeft + trackW * initialValue;
    const handle = this.add.circle(handleX, y, 15, COLORS.pearlWhite).setDepth(6);
    handle.setStrokeStyle(3, COLORS.aquaLight);
    handle.setInteractive({ useHandCursor: true, draggable: true });

    const handleGlow = this.add.circle(handleX, y, 24, COLORS.aquaLight, 0.18).setDepth(5);

    let value = initialValue;
    const setValue = (v: number) => {
      value = Phaser.Math.Clamp(v, 0, 1);
      const x = trackLeft + trackW * value;
      handle.x = x;
      handleGlow.x = x;
      drawFill(value);
      pctText.setText(`${Math.round(value * 100)}%`);
      onChange(value);
    };

    this.input.on('drag', (_pointer: Phaser.Input.Pointer, obj: Phaser.GameObjects.GameObject, dragX: number) => {
      if (obj !== handle) return;
      const clampedX = Phaser.Math.Clamp(dragX, trackLeft, trackLeft + trackW);
      setValue((clampedX - trackLeft) / trackW);
    });

    handle.on('pointerover', () => this.setFocus(this.focusables.indexOf(focusable)));

    // Focus box spans the full row — track + handle + the % readout on the
    // right — so the selection frame encloses the percentage instead of
    // stopping short of it. Right edge padded for the widest value ("100%").
    // The label sits 40px above, clear of the halo's top edge.
    const frameLeft = trackLeft;
    const frameRight = pctCenterX + 42;
    const focusable: Focusable = {
      x: (frameLeft + frameRight) / 2,
      y,
      w: frameRight - frameLeft,
      h: 20,
      focus: (on) => handle.setStrokeStyle(on ? 4 : 3, on ? 0xFFFFFF : COLORS.aquaLight),
      left:  () => setValue(value - 0.05),
      right: () => setValue(value + 0.05),
    };
    return focusable;
  }

  private createToggle(
    cx: number,
    y: number,
    label: string,
    initialValue: boolean,
    onChange: (value: boolean) => void,
  ): Focusable {
    let isOn = initialValue;
    const toggleW = 128;
    const toggleH = 52;

    // Lay the label + pill out as one centered group: measure the label,
    // then place it a short gap to the left of the pill and centre the whole
    // unit on cx so the row reads as balanced.
    const labelText = this.add.text(0, y, label, {
      ...TEXT_PRESET.label,
      fontSize: '26px',
      color: COLORS_HEX.aquaLight,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0, 0.5).setDepth(5);

    const gap = 30;
    const groupW = labelText.width + gap + toggleW;
    const groupLeft = cx - groupW / 2;
    labelText.setX(groupLeft);
    const toggleX = groupLeft + labelText.width + gap + toggleW / 2;

    const knobR = toggleH / 2 - 6;
    // Knob parks at whichever end matches the state; the ON/OFF caption sits
    // in the open half opposite the knob so the two never overlap.
    const knobOffX = (on: boolean) => on
      ? toggleX + toggleW / 2 - toggleH / 2
      : toggleX - toggleW / 2 + toggleH / 2;
    const textOffX = (on: boolean) => on
      ? toggleX - toggleH / 2 + 6
      : toggleX + toggleH / 2 - 6;

    const toggleBg = this.add.graphics().setDepth(4);
    const drawToggleBg = (on: boolean) => {
      toggleBg.clear();
      toggleBg.fillStyle(on ? COLORS.aqua : COLORS.deepOcean, on ? 0.62 : 0.9);
      toggleBg.fillRoundedRect(toggleX - toggleW / 2, y - toggleH / 2, toggleW, toggleH, toggleH / 2);
      toggleBg.fillStyle(COLORS.pearlWhite, on ? 0.08 : 0.04);
      toggleBg.fillRoundedRect(toggleX - toggleW / 2 + 4, y - toggleH / 2 + 4, toggleW - 8, toggleH * 0.36, toggleH / 2 - 4);
      toggleBg.lineStyle(1.5, on ? COLORS.aquaLight : COLORS.moonlitFoam, 0.45);
      toggleBg.strokeRoundedRect(toggleX - toggleW / 2, y - toggleH / 2, toggleW, toggleH, toggleH / 2);
    };
    drawToggleBg(isOn);

    const knob = this.add.circle(knobOffX(isOn), y, knobR, COLORS.pearlWhite).setDepth(5);
    knob.setStrokeStyle(2, COLORS.aquaLight);

    const toggleText = this.add.text(textOffX(isOn), y, isOn ? 'ON' : 'OFF', {
      ...TEXT_PRESET.label,
      fontSize: '22px',
      color: COLORS_HEX.goldLight,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(6);

    const hitArea = this.add.rectangle(toggleX, y, toggleW + 20, toggleH + 20, 0x000000, 0).setDepth(7);
    hitArea.setInteractive({ useHandCursor: true });

    const flip = () => {
      isOn = !isOn;
      drawToggleBg(isOn);
      toggleText.setText(isOn ? 'ON' : 'OFF');
      toggleText.setX(textOffX(isOn));
      this.tweens.add({ targets: knob, x: knobOffX(isOn), duration: 150, ease: 'Back.easeOut' });
      onChange(isOn);
    };
    hitArea.on('pointerdown', flip);
    hitArea.on('pointerover', () => this.setFocus(this.focusables.indexOf(focusable)));

    const focusable: Focusable = {
      x: cx, y, w: groupW + 36, h: 64,
      focus: (on) => knob.setStrokeStyle(on ? 3 : 2, on ? 0xFFFFFF : COLORS.aquaLight),
      confirm: flip,
      left:  flip,
      right: flip,
    };
    return focusable;
  }
}
